/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationInfoProcessed.css";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";

class DonationInfoProcessed extends Component {
  constructor(props) {
    super(props);
    if (this.props.location.state === null) {
      this.props.history.push("/");
    }

    this.state = {
      ...this.props.location.state,
      showFeeBreakdown: false
    };
  }

  handleToggleFeeBreakdown = () => {
    this.setState(prevState => ({
      showFeeBreakdown: !prevState.showFeeBreakdown
    }));
  };

  calculateFeeBreakdown = () => {
    const { data } = this.state;

    const processingFee = parseFloat(data.processingFee) || 0;
    const serviceFee = parseFloat(data.serviceFee) || 0;
    const totalFees = processingFee + serviceFee;

    // Debug logging
    // console.log('[DonationInfoProcessed] Fee breakdown data:', {
    // processingFee: data.processingFee,
    // serviceFee: data.serviceFee,
    // coverFee: data.coverFee,
    // net: data.net,
    // amount: data.amount,
    // calculatedProcessingFee: processingFee,
    // calculatedServiceFee: serviceFee,
    // totalFees: totalFees,
    // hasFees: totalFees > 0,
    // isManualPayment: !data.processingFee && !data.serviceFee
    // });

    return {
      processingFee,
      serviceFee,
      totalFees,
      hasFees: totalFees > 0,
      isOnlinePayment: !this.isManualPayment(data)
    };
  };

  isManualPayment = (data) => {
    return !data.processingFee && !data.serviceFee;
  };

  renderCurrencyValue = (value) => {
    return (
      <NumericFormat
        decimalSeparator="."
        displayType="text"
        valueIsNumericString={true}
        value={value}
        allowNegative={false}
        thousandSeparator={true}
        prefix={"$"}
        fixedDecimalScale={true}
        decimalScale={2}
      />
    );
  };

  getProcessingFeeLabel = () => {
    const { data } = this.state;
    const languageObj = this.props.languageObj;

    // Determine payment processor based on PaymentMode
    switch(data.PaymentMode) {
      case 'Stripe':
        return languageObj.stripeProcessingFee || 'Stripe Processing Fee';
      case 'PayMe':
        return languageObj.hsbcProcessingFee || 'HSBC Processing Fee';
      default:
        return languageObj.processingFee || 'Processing Fee';
    }
  };

  renderFeeBreakdown = () => {
    const { data } = this.state;
    const { processingFee, serviceFee, totalFees, hasFees, isOnlinePayment } = this.calculateFeeBreakdown();
    const languageObj = this.props.languageObj;

    // Only show fee breakdown when donor covered the fees
    if (!hasFees || !isOnlinePayment || !data.coverFee) {
      return null;
    }

    const processingFeeLabel = this.getProcessingFeeLabel();

    const totalFeesElement = (
      <div className="DonationInfoProcessed-item">
        {languageObj.totalFees || "Total Fees"}
        <div className="DonationInfoProcessed-answer">
          HK{this.renderCurrencyValue(totalFees)}
        </div>
        {hasFees && (
          <button
            type="button"
            className="DonationInfoProcessed-feeToggleBtn"
            onClick={this.handleToggleFeeBreakdown}
            aria-expanded={this.state.showFeeBreakdown}
          >
            {this.state.showFeeBreakdown
              ? (languageObj.hideFeeBreakdown || "Hide breakdown")
              : (languageObj.viewFeeBreakdown || "View breakdown")
            }
          </button>
        )}
      </div>
    );

    const breakdownElement = this.state.showFeeBreakdown ? (
      <>
        {processingFee > 0 && (
          <div className="DonationInfoProcessed-item DonationInfoProcessed-item--nested">
            {processingFeeLabel}
            <div className="DonationInfoProcessed-answer">
              HK{this.renderCurrencyValue(processingFee)}
            </div>
          </div>
        )}
        {serviceFee > 0 && (
          <div className="DonationInfoProcessed-item DonationInfoProcessed-item--nested">
            {languageObj.checkPlusPlatformFee || languageObj.serviceFee || "CheckPlus Platform Fee"}
            <div className="DonationInfoProcessed-answer">
              HK{this.renderCurrencyValue(serviceFee)}
            </div>
          </div>
        )}
        {data.coverFee && (
          <div className="DonationInfoProcessed-item DonationInfoProcessed-item--nested DonationInfoProcessed-item--highlight">
            {languageObj.donorCoveredFees || "Donor Covered Fees"}
            <div className="DonationInfoProcessed-answer-note">
              {languageObj.feesCoveredNote || "This donor chose to cover the transaction fees."}
            </div>
          </div>
        )}
      </>
    ) : null;

    return (
      <>
        {totalFeesElement}
        {breakdownElement}
      </>
    );
  };

  renderNetAmount = () => {
    const { data } = this.state;
    const languageObj = this.props.languageObj;

    // Only show net amount when donor covered fees (part of fee breakdown)
    if (this.isManualPayment(data) || !data.coverFee) {
      return null;
    }

    return (
      <div className="DonationInfoProcessed-item">
        {languageObj.netAmount || "Net Amount Received"}
        <div className="DonationInfoProcessed-answer">
          HK{this.renderCurrencyValue(data.net || data.amount)}
        </div>
      </div>
    );
  };

  render() {
    const languageObj = this.props.languageObj;

    const data = this.state.data;

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    const RenderReceiptBtn = () => {
      const url = this.state.data.receiptUrl;
      if (data.receiptVoidedAt || data.receiptStatus === 'voided') return <p role="status">{languageObj.receiptVoided}</p>;
      if (!url) return null;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <div className="DonationInfoProcessed-receiptContainer">
            <div className="DonationInfoProcessed-receiptIcn" />
            <div className="DonationInfoProcessed-receiptTxt">
              {languageObj.receipt}
            </div>
          </div>
        </a>
      );
    };

    return (
      <div className="DonationInfoProcessed-view">
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationInfoProcessed-bg" style={headerStyle} />
        <div className="DonationInfoProcessed-container">
          <div className="DonationInfoProcessed-header">
            <Link
              to="/Transactions"
              className="DonationInfoProcessed-backContainer"
            >
              <div className="DonationInfoProcessed-back">
                <div className="DonationInfoProcessed-backBtn"></div>
                <div className="DonationInfoProcessed-backText">
                  {languageObj.backBtn}
                </div>
              </div>
            </Link>

            <RenderReceiptBtn />
          </div>

          <div className="DonationInfoProcessed-main">
            <div className="DonationInfoProcessed-inputContainer">
              <div className="DonationInfoProcessed-heading">
                {this.state.orgName}
              </div>

              {data.organizationReceiptId && (
                <div className="DonationInfoProcessed-item">
                  {languageObj.receiptNumber}
                  <div className="DonationInfoProcessed-answer">
                    {data.organizationReceiptId}
                  </div>
                </div>
              )}

              <div className="DonationInfoProcessed-item">
                {languageObj.processedDate}
                <div className="DonationInfoProcessed-answer">
                  {this.state.date}
                </div>
              </div>

              <div className="DonationInfoProcessed-item">
                {languageObj.item}
                <div className="DonationInfoProcessed-answer">
                  {data.projectName}
                </div>
              </div>

              <div className="DonationInfoProcessed-item">
                {languageObj.amount}
                <div className="DonationInfoProcessed-answer">
                  HK
                  <NumericFormat
                    decimalSeparator="."
                    displayType="text"
                    valueIsNumericString={true}
                    value={data.amount}
                    allowNegative={false}
                    thousandSeparator={true}
                    prefix={"$"}
                    fixedDecimalScale={true}
                    decimalScale={2}
                  />
                </div>
              </div>

              {this.renderFeeBreakdown()}
              {this.renderNetAmount()}

              <div className="DonationInfoProcessed-item">
                {languageObj.paymentMethod}
                <div className="DonationInfoProcessed-answer">
                  {data.cardBrand}{data.cardLast4 ? ` - ${data.cardLast4}` : ''}
                </div>
              </div>

              <div className="DonationInfoProcessed-item">
                {languageObj.memo}
                <div className="DonationInfoProcessed-answer">
                  {!data.memo ? "..." : data.memo}
                </div>
              </div>
            </div>

            <div className="DonationInfoProcessed-chin" />
          </div>
        </div>
      </div>
    );
  }
}
DonationInfoProcessed.propTypes = {
  languageObj: PropTypes.object,
};
export default DonationInfoProcessed;
